/**
 * 进一步封装滚动加载组件
 *
 * @example
 * pages/adaption/[id].tsx
 * ```js
  const {Comp, InfiniteScollComp, list, hasNextPage } =
    useInfiniteScroll<
      getAdaptionArtwork_itemModule_adaptionArtworkPage_edges_node,
      getAdaptionArtwork,
      getAdaptionArtworkVariables
    >({
      queryDoc: GET_ADAPTION_ARTWORK,
      initData: list,
      hasNextPage,
      hasNextPageExtractor: data => {
        return (
          data.data.itemModule?.adaptionArtworkPage?.pageInfo?.hasNextPage ||
          false
        )
      },
      dataExtractor: data => {
        return getGraphData<getAdaptionArtwork_itemModule_adaptionArtworkPage_edges_node>(
          data.data.itemModule?.adaptionArtworkPage?.edges || []
        )
      },
    })

  return(
    <>
      {Comp}
      {InfiniteScollComp}
    </>
  )
 * ```
 */

import { ApolloQueryResult, DocumentNode } from '@apollo/client'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { InfiniteScroll } from '~/components/business/InfiniteScroll'
import { PAGE_SIZE } from '~/configs'
import { uniqueArray } from '~/utils'
import { useImperativeQuery } from './useImperativeQuery'

interface Params<T, TData, TVariables> {
  /** gql query document */
  queryDoc: DocumentNode
  /** 除了offset, first外，额外的query参数 */
  extraQueryParams?: Partial<TVariables>
  /** 初始化数据，小于 pageSize 且 hasNextPage 时立即调用一次 onScroll */
  initData: T[]
  /** 翻页数量，搜索页面布局问题，使用3和4的倍数 */
  initPageSize?: number
  /** 是否有下一页，不确定的时候填true */
  hasNextPage: boolean
  /** 用于从ApolloQueryResult<TData>取出hasNextPage字段 */
  hasNextPageExtractor: (arg: ApolloQueryResult<TData>) => boolean
  /** 用于从ApolloQueryResult<TData>取出data字段, 合并进list */
  dataExtractor: (arg: ApolloQueryResult<TData>) => T[]
  /** 数据合并去重字段，不传默认id, 透传到uniqueArray */
  dataUniqueBy?: (it: any) => any
  /** loading是否展示正在加载的文案 */
  showLoadingText?: boolean
}

export default function useInfiniteScroll<T, TData, TVariables>({
  queryDoc,
  extraQueryParams,
  initData,
  hasNextPage: _hasNextPage,
  initPageSize = PAGE_SIZE,
  hasNextPageExtractor,
  dataExtractor,
  dataUniqueBy,
  showLoadingText,
}: Params<T, TData, TVariables>) {
  const [list, setList] = useState(initData)
  const [loading, setLoading] = useState(false)
  const [hasNextPage, setHasNextPage] = useState(_hasNextPage)
  const [pageInfo, setPageInfo] = useState({
    page: Math.floor(initData.length / initPageSize),
    pageSize: initPageSize,
  })

  const queryFunc = useImperativeQuery<TData, TVariables>(queryDoc)

  useEffect(() => {
    /** 页面中initData改变了，一般由页面tab切换触发 */
    setList(initData)
    setLoading(false)
    setHasNextPage(_hasNextPage)
    setPageInfo({
      // ceil避免被去重之后，数量少了，导致翻页offset没有增加
      page: Math.ceil(initData.length / initPageSize),
      pageSize: initPageSize,
    })
  }, [initData])

  const onScroll = useCallback(() => {
    // fix: 第一次进入的时候 onScroll 触发两次
    if (loading) {
      return
    }
    setLoading(true)
    queryFunc({
      offset: pageInfo.pageSize * pageInfo.page,
      first: pageInfo.pageSize,
      ...extraQueryParams,
    } as unknown as TVariables)
      .then(data => {
        setPageInfo(page => {
          return {
            pageSize: page.pageSize,
            page: page.page + 1,
            ...extraQueryParams,
          }
        })

        setHasNextPage(hasNextPageExtractor(data) || false)

        setList(list => {
          return uniqueArray(list, dataExtractor(data), dataUniqueBy)
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [queryFunc, pageInfo, initData, loading])

  useEffect(() => {
    if (Math.floor(initData.length / initPageSize) === 0 && hasNextPage) {
      onScroll()
    }
  }, [initData, hasNextPage])

  const onDelete = useCallback(
    (willDeletedArr: T[]) => {
      queryFunc({
        offset: Math.max(list.length - willDeletedArr.length, 0),
        first: willDeletedArr.length,
        ...extraQueryParams,
      } as unknown as TVariables)
        .then(data => {
          setHasNextPage(hasNextPageExtractor(data) || false)

          setList(list => {
            const newList = list.filter((item: any) => {
              return !willDeletedArr.some((subIt: any) =>
                !!dataUniqueBy
                  ? dataUniqueBy?.(item) === dataUniqueBy?.(subIt)
                  : item?.id === subIt?.id
              )
            })
            return uniqueArray(newList, dataExtractor(data), dataUniqueBy)
          })
        })
        .catch(e => {
          /** 这个错误不重要，catch住也没关系 */
        })
    },
    [queryFunc, list]
  )

  const onChange = useCallback((data: T[]) => {
    setList(data)
  }, [])

  const InfiniteScollComp = useMemo(
    () => (
      <InfiniteScroll
        onScroll={onScroll}
        hasNextPage={hasNextPage}
        loading={hasNextPage || loading}
        showLoadingText={showLoadingText}
      />
    ),
    [onScroll, hasNextPage, loading, showLoadingText]
  )

  return {
    /** 封装后的 InfiniteScoll */
    InfiniteScollComp,
    /** 合并后的数据列表，一般用不到 */
    list,
    /** 是否有下一页，一般用不到 */
    hasNextPage,
    /** 删除list中的条目n条后，调用该函数，在list中补全n条数据 */
    onDelete,
    /** 用户操作变更某些数据 */
    onChange,
    onScroll,
    loading,
    setList,
  }
}
